import { config } from '@mybonus/public';

import { useAPI, useMutation, type APIInput, type APIOutput } from '../api';
import { useAccountContext } from '../context';

export function useWithdrawalFlow() {
	const account = useAccountContext();
	const { api } = useAPI();

	const withdrawal = useMutation<
		APIInput['user']['withdrawal']['create'],
		APIOutput['user']['withdrawal']['create']
	>((args) => api.user.withdrawal.create.mutate(args), {
		onSuccess: () => {
			account.invalidate();
		},
	});

	function doWithdrawal() {
		withdrawal.mutate({ vendor: 'TRUSTLY' });
	}

	const { cashbackSummary, ongoingWithdrawalRequest } = account.overview.data || {};

	const isWithdrawable = cashbackSummary?.amountUntilWithdrawalLimit.value === 0;
	const withdrawalLimitProgress = Math.min(
		100,
		((cashbackSummary?.amountUntilWithdrawalLimit.value || 0) /
			config.minimumCashbackForWithdrawal) *
			100,
	);

	function getStateText(): { btn: string; desc: string } {
		if (ongoingWithdrawalRequest) {
			if (ongoingWithdrawalRequest.status === 'PENDING') {
				return {
					btn: 'Betala ut',
					desc: 'Redo för utbetalning',
				};
			} else if (ongoingWithdrawalRequest.status === 'REVIEW_DENIED') {
				return {
					btn: 'Bearbetas...',
					desc: 'Utbetalningen nekades',
				};
			} else if (ongoingWithdrawalRequest.status === 'IN_REVIEW') {
				return {
					btn: 'Bearbetas...',
					desc: 'Utbetalningen granskas',
				};
			} else {
				// note: should never come here
				return {
					btn: '',
					desc: '',
				};
			}
		} else {
			if (isWithdrawable) {
				return {
					btn: 'Betala ut',
					desc: 'Redo för utbetalning',
				};
			} else {
				return {
					btn: 'Betala ut',
					desc: `${cashbackSummary?.amountUntilWithdrawalLimit.text} till uttagsgränsen`,
				};
			}
		}
	}

	const isWithdrawalButtonDisabled =
		withdrawal.isLoading ||
		!account.overview.data?.cashbackSummary ||
		!isWithdrawable ||
		!account.isIdentified ||
		(!!ongoingWithdrawalRequest && ongoingWithdrawalRequest.status !== 'PENDING');

	return {
		overview: account.overview,
		withdrawal,
		isUserIdentified: account.isIdentified,
		doWithdrawal,
		isWithdrawable,
		cashbackSummary,
		stateText: getStateText(),
		isWithdrawalButtonDisabled,
		ongoingWithdrawalRequest,
		withdrawalLimitProgress,
	};
}
